import { Close as CloseIcon, Key, Send as SendIcon, Subject as SubjectIcon } from '@mui/icons-material';
import { Box, Button, InputAdornment, Stack, TextField } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IdIssuanceIssueFormInputs, PendingRespondent } from 'src/@types/pendingRespondentStatus';
import { useIdIssuanceManagementDetailForm } from './hooks/useIdIssuanceManagementDetailForm';

type IdIssuanceManagementDetailFormProps = {
  selectedUser: PendingRespondent;
  setSelectedUser: Dispatch<SetStateAction<PendingRespondent | undefined>>;
};

export type InputForm = IdIssuanceIssueFormInputs & { password: string };

export const IdIssuanceManagementDetailForm = ({ selectedUser, setSelectedUser }: IdIssuanceManagementDetailFormProps) => {
  const { t } = useTranslation();
  const { onSubmit } = useIdIssuanceManagementDetailForm({ selectedUser, setSelectedUser });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
    watch,
  } = useForm<InputForm>({
    mode: 'onChange',
    defaultValues: {
      subject: selectedUser.emailTemplate?.title,
      password: '',
      content: selectedUser.emailTemplate?.content,
    },
  });

  const isFormValid = isValid && isDirty;
  const password = watch('password');
  const content = watch('content');
  const contentWithPassword = content && content.replaceAll('{PASSWORD}', password);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Controller
          name="password"
          control={control}
          rules={{
            required: t('idIssuanceManagement.detail.form.password.required', {
              target: t('idIssuanceManagement.detail.form.password.label'),
            }),
            maxLength: { value: 32, message: t('idIssuanceManagement.detail.form.password.maxLength', { max: 32 }) },
            minLength: { value: 8, message: t('idIssuanceManagement.detail.form.password.minLength', { min: 8 }) },
            pattern: {
              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{0,}$/,
              message: t('idIssuanceManagement.detail.form.password.pattern'),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('idIssuanceManagement.detail.form.password.label')}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Key color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Controller
          name="subject"
          control={control}
          rules={{
            required: t('idIssuanceManagement.detail.form.subject.required', { target: t('idIssuanceManagement.detail.form.subject.label') }),
            maxLength: { value: 200, message: t('idIssuanceManagement.detail.form.subject.maxLength', { max: 200 }) },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('idIssuanceManagement.detail.form.subject.label')}
              error={!!errors.subject}
              helperText={errors.subject?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SubjectIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Controller
          name="content"
          control={control}
          rules={{
            required: t('idIssuanceManagement.detail.form.content.required', { target: t('idIssuanceManagement.detail.form.content.label') }),
            maxLength: { value: 5000, message: t('idIssuanceManagement.detail.form.content.maxLength', { max: 5000 }) },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              value={contentWithPassword}
              label={t('idIssuanceManagement.detail.form.content.label')}
              multiline
              rows={20}
              error={!!errors.content}
              helperText={errors.content?.message}
            />
          )}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
          }}>
          <Button variant="outlined" onClick={() => reset()} startIcon={<CloseIcon />}>
            {t('idIssuanceManagement.detail.form.button.clear')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendIcon />}
            sx={{ px: 4 }}
            disabled={selectedUser.status !== 'AGREED' || isSubmitting || !isFormValid}>
            {t('idIssuanceManagement.detail.form.button.submit')}
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
